import React, { useMemo } from 'react';

import { Dropdown, Flex, type MenuProps, Tooltip } from 'antd-v5';
import { castArray } from 'lodash';
import { MdContentPaste, MdExpandMore } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { DarkToolbarButton } from 'scouting-ui/lib/Button';

import {
  navigateToBulkAwards,
  navigateToBulkEntryAdventure,
  navigateToBulkEntryMB,
} from '@modules/bulkEntry/duck/actions';
import {
  shouldShowAdventuresBulkEntry,
  shouldShowAwardsBulkEntry,
  shouldShowMeritBadgeBulkEntry,
  shouldShowRankBulkEntry,
  shouldShowYouthLeadershipBulkEntry,
} from '@modules/bulkEntry/duck/utils/featureFlags';
import { programSel } from '@modules/context/duck/selectorsTyped';
import { navigateToBulkRemoveEvents } from '@modules/events/duck/actionsTyped';
import { routesMapSel } from '@modules/location/duck/selectorsTyped';
import { Program } from '@shared/constants';
import {
  navigateToQuickEntryHealth,
  navigateToQuickEntryOA,
  navigateToQuickEntryRank,
  navigateToQuickEntrySchool,
  navigateToQuickEntryScoutLeadership,
  navigateToQuickEntrySwimming,
} from '@shared/duck/actions';
import { newPermissionsSel } from '@shared/duck/selectors/general.selectors';

interface QuickEntryItem {
  key: string;
  labelId: string;
  isShown?: (program: string) => boolean;
  navigate: (unitGuid: string) => { type: string };
}

const ITEMS: QuickEntryItem[] = [
  {
    key: 'rank',
    labelId: 'packRoster.QuickEntryDropdown.qeRank',
    isShown: shouldShowRankBulkEntry,
    navigate: unitGuid => navigateToQuickEntryRank({ unitGuid }),
  },
  {
    key: 'meritBadges',
    labelId: 'packRoster.QuickEntryDropdown.bulkEntryMB',
    isShown: shouldShowMeritBadgeBulkEntry,
    navigate: unitGuid => navigateToBulkEntryMB({ unitGuid }),
  },
  {
    key: 'adventures',
    labelId: 'packRoster.QuickEntryDropdown.bulkEntryAdventures',
    isShown: shouldShowAdventuresBulkEntry,
    navigate: unitGuid => navigateToBulkEntryAdventure({ unitGuid }),
  },
  {
    key: 'awards',
    labelId: 'packRoster.QuickEntryDropdown.bulkEntryAwards',
    isShown: shouldShowAwardsBulkEntry,
    navigate: unitGuid => navigateToBulkAwards({ unitGuid }),
  },
  {
    key: 'calendarEvents',
    labelId: 'packRoster.QuickEntryDropdown.qeDeleteEvents',
    navigate: () => navigateToBulkRemoveEvents(),
  },
  {
    key: 'health',
    labelId: 'packRoster.QuickEntryDropdown.heathRecord',
    navigate: () => navigateToQuickEntryHealth(),
  },
  {
    key: 'swimming',
    labelId: 'packRoster.QuickEntryDropdown.swimmerClass',
    navigate: () => navigateToQuickEntrySwimming(),
  },
  {
    key: 'scoutLeadership',
    labelId: 'packRoster.QuickEntryDropdown.scoutLeadership',
    isShown: shouldShowYouthLeadershipBulkEntry,
    navigate: () => navigateToQuickEntryScoutLeadership(),
  },
  {
    key: 'oa',
    labelId: 'packRoster.QuickEntryDropdown.OaData',
    isShown: program => program !== Program.CUB_SCOUT,
    navigate: () => navigateToQuickEntryOA(),
  },
  {
    key: 'school',
    labelId: 'packRoster.QuickEntryDropdown.schoolInfo',
    navigate: () => navigateToQuickEntrySchool(),
  },
];

// The router's rule (`checkAccess`): any one of the route's permissions
function canOpenRoute(
  route: { permission?: string | string[] } | undefined,
  permissions: Record<string, boolean | undefined>,
) {
  return (
    !route?.permission ||
    castArray(route.permission).some(permission => permissions[permission])
  );
}

interface QuickEntryDropdownProps {
  orgGuid: string;
}

export default function QuickEntryDropdown(props: QuickEntryDropdownProps) {
  const { orgGuid } = props;
  const dispatch = useDispatch();
  const program = useSelector(programSel);
  const routesMap = useSelector(routesMapSel);
  const permissions = useSelector(newPermissionsSel);

  const entries = useMemo(
    () =>
      ITEMS.filter(item => !item.isShown || item.isShown(program)).map(item => {
        const action = item.navigate(orgGuid);
        return {
          ...item,
          action,
          disabled: !canOpenRoute(routesMap?.[action.type], permissions),
        };
      }),
    [program, orgGuid, routesMap, permissions],
  );

  const menu = useMemo<MenuProps>(
    () => ({
      items: entries.map(({ key, labelId, disabled }) => ({
        key,
        disabled,
        label: disabled ? (
          <Tooltip
            placement="left"
            title={<FormattedMessage id="rosterV2.quickEntry.noPermission" />}
          >
            {/* Fills the row, so hovering past the text still shows the tooltip */}
            <Flex component="span">
              <FormattedMessage id={labelId} />
            </Flex>
          </Tooltip>
        ) : (
          <FormattedMessage id={labelId} />
        ),
      })),
      onClick: ({ key }) => {
        const entry = entries.find(item => item.key === key);
        if (entry) dispatch(entry.action);
      },
    }),
    [entries, dispatch],
  );

  return (
    <Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
      <DarkToolbarButton icon={<MdContentPaste />}>
        <FormattedMessage id="packRoster.QuickEntryDropdown.quickEntry" />
        <MdExpandMore />
      </DarkToolbarButton>
    </Dropdown>
  );
}
